import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Minus, Spline, GitFork, Square, Circle, Ruler, Eraser, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import { ShapeRenderer } from "./ShapeRenderer";
import { ShapeProperties } from "./ShapeProperties";
import { newShape, shapeToLineItem } from "./shapes";
import { exportDesignPng } from "./exportPng";
import { uid } from "../../lib/format";

const TOOLS = [
  { type: "pipe", label: "Pipe", icon: Minus },
  { type: "bend", label: "Bend", icon: Spline },
  { type: "tee", label: "Tee", icon: GitFork },
  { type: "rect", label: "Rectangle", icon: Square },
  { type: "circle", label: "Circle", icon: Circle },
];
const VB = { w: 1200, h: 640 };
const GRID = 20;

export const ShapeDesigner = forwardRef(({ design, onChange, onAddLineItem }, ref) => {
  const svgRef = useRef(null);
  const contentRef = useRef(null);
  const drag = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showDims, setShowDims] = useState(true);
  const shapes = design.shapes || [];
  const scale = design.scale || 0.4;
  const selected = shapes.find((s) => s.id === selectedId);

  useImperativeHandle(ref, () => ({
    exportPng: async () => {
      setSelectedId(null);
      await new Promise((r) => setTimeout(r, 30));
      return exportDesignPng(svgRef.current, contentRef.current);
    },
  }));

  const setShapes = (next) => onChange({ ...design, shapes: next });
  const update = (s) => setShapes(shapes.map((x) => (x.id === s.id ? s : x)));
  const toSvg = (e) => {
    const pt = svgRef.current.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    return pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
  };

  const add = (type) => {
    const s = newShape(type, 160 + (shapes.length % 6) * 40, 140 + (shapes.length % 6) * 40);
    setShapes([...shapes, s]);
    setSelectedId(s.id);
  };
  const onDown = (e, s) => {
    e.stopPropagation();
    const p = toSvg(e);
    drag.current = { id: s.id, dx: p.x - s.x, dy: p.y - s.y };
    setSelectedId(s.id);
    svgRef.current.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const p = toSvg(e);
    const snap = (v) => Math.round(v / (GRID / 2)) * (GRID / 2);
    setShapes(shapes.map((x) => (x.id === drag.current.id ? { ...x, x: snap(p.x - drag.current.dx), y: snap(p.y - drag.current.dy) } : x)));
  };
  const onUp = () => { drag.current = null; };

  const zoom = (f) => onChange({ ...design, scale: Math.min(1, Math.max(0.05, +(scale * f).toFixed(3))) });

  return (
    <div data-testid="shape-designer" className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-zinc-200 bg-zinc-950 px-3 py-2">
        {TOOLS.map(({ type, label, icon: Icon }) => (
          <button key={type} data-testid={`designer-add-${type}`} onClick={() => add(type)}
            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-zinc-200 transition-colors hover:bg-red-600 hover:text-white">
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 text-zinc-300">
          <button data-testid="designer-zoom-out" onClick={() => zoom(0.8)} className="rounded p-1.5 hover:bg-zinc-800"><ZoomOut className="h-4 w-4" /></button>
          <span data-testid="designer-scale" className="w-24 text-center font-mono text-xs">1px = {(1 / scale).toFixed(1)}mm</span>
          <button data-testid="designer-zoom-in" onClick={() => zoom(1.25)} className="rounded p-1.5 hover:bg-zinc-800"><ZoomIn className="h-4 w-4" /></button>
          <button data-testid="designer-toggle-dims" onClick={() => setShowDims(!showDims)} className={`ml-2 flex items-center gap-1 rounded px-2 py-1.5 text-xs ${showDims ? "bg-zinc-800 text-white" : "hover:bg-zinc-800"}`}><Ruler className="h-4 w-4" />Dims</button>
          <button data-testid="designer-clear" onClick={() => { if (window.confirm("Clear all shapes?")) { setShapes([]); setSelectedId(null); } }} className="flex items-center gap-1 rounded px-2 py-1.5 text-xs hover:bg-zinc-800"><Eraser className="h-4 w-4" />Clear</button>
        </div>
      </div>
      <div className="grid grid-cols-[1fr_280px]">
        <div className="relative blueprint">
          <svg ref={svgRef} data-testid="designer-canvas" viewBox={`0 0 ${VB.w} ${VB.h}`} className="block h-[560px] w-full touch-none select-none"
            onPointerMove={onMove} onPointerUp={onUp} onPointerDown={() => setSelectedId(null)}>
            <defs>
              <pattern id="grid-sm" width={GRID} height={GRID} patternUnits="userSpaceOnUse"><path d={`M ${GRID} 0 L 0 0 0 ${GRID}`} fill="none" stroke="#E2E8F0" strokeWidth="0.6" /></pattern>
              <pattern id="grid-lg" width={GRID * 5} height={GRID * 5} patternUnits="userSpaceOnUse"><rect width={GRID * 5} height={GRID * 5} fill="url(#grid-sm)" /><path d={`M ${GRID * 5} 0 L 0 0 0 ${GRID * 5}`} fill="none" stroke="#CBD5E1" strokeWidth="1" /></pattern>
            </defs>
            <rect data-export-hide x={-5000} y={-5000} width={10000} height={10000} fill="url(#grid-lg)" />
            <g ref={contentRef}>
              {shapes.map((s) => (
                <g key={s.id} data-testid={`canvas-shape-${s.type}`} transform={`translate(${s.x} ${s.y}) rotate(${s.rotation})`} onPointerDown={(e) => onDown(e, s)} className="cursor-move">
                  <ShapeRenderer shape={s} scale={scale} selected={s.id === selectedId} showDims={showDims} />
                </g>
              ))}
            </g>
          </svg>
          {!shapes.length && <div className="pointer-events-none absolute inset-0 grid place-items-center text-sm text-zinc-400">Add pipes and shapes from the toolbar, then drag to arrange.</div>}
          <div className="pointer-events-none absolute bottom-2 left-3 font-mono text-[10px] uppercase tracking-widest text-zinc-400">Dimensions in mm · Guide only</div>
        </div>
        <div className="border-l border-zinc-200 bg-slate-50">
          <ShapeProperties shape={selected} onChange={update}
            onDelete={() => { setShapes(shapes.filter((x) => x.id !== selectedId)); setSelectedId(null); }}
            onDuplicate={() => { const c = { ...selected, id: uid(), x: selected.x + 40, y: selected.y + 40, dims: { ...selected.dims } }; setShapes([...shapes, c]); setSelectedId(c.id); }}
            onAddLineItem={() => { onAddLineItem(shapeToLineItem(selected)); toast.success("Line item added — set the rate in Items & Pricing"); }} />
        </div>
      </div>
    </div>
  );
});
